import axiosInstance from 'api/axiosConfig'
import { ENDPOINTS } from 'api/endpoints'
import {
  ApplicationPipelineStatus,
  ListingManagerStatus,
  VisitRequestStatus,
  VisitSlotStatus,
} from 'types/workflowStatus'

export type {
  ApplicationPipelineStatus,
  ListingManagerStatus,
  VisitRequestStatus,
  VisitSlotStatus,
}

export interface ListingManagerPayload {
  listing: {
    id: number | string
    title?: string | null
    status: ListingManagerStatus
    price: string | null
    availability: string | null
    contractEnd: string | null
    createdAt: string | null
  }
  metrics: {
    favorites: number
    applications: number
    visitsSlots: number
    visitsRequests: number
    contacts: number
  }
  visitSlots: Array<{
    id: number
    listing_id: number
    start_at: string
    end_at: string
    capacity: number
    status: VisitSlotStatus
    notes?: string | null
  }>
  visitRequests: Array<{
    id: number
    slot_id: number
    listing_id: number
    applicant_user_fk?: string | null
    applicant_email?: string | null
    applicant_name?: string | null
    status: VisitRequestStatus
    message?: string | null
    slot_start_at?: string | null
    slot_end_at?: string | null
  }>
  applications: Array<{
    id: number
    property_fk: number
    applicant_user_fk?: string | null
    applicant_name?: string | null
    applicant_email?: string | null
    move_in_date?: string | null
    income_range?: string | null
    occupants?: number | null
    has_pets?: string | null
    message?: string | null
    application_status?: ApplicationPipelineStatus
    notes_owner?: string | null
    owner_message?: string | null
    created_at?: string
  }>
  applicationEvents: Array<{
    id: number
    application_id: number
    event_type: string
    from_status?: string | null
    to_status?: string | null
    message?: string | null
    created_by?: string | null
    created_at: string
  }>
}

type ApiResponse<T> = {
  success: boolean
  message?: string
  data?: T
}

function ensureSuccess<T>(res: ApiResponse<T>): T {
  if (!res?.success || !res.data) {
    throw new Error(res?.message || 'Request failed')
  }
  return res.data
}

export type OwnerDashboardSummary = {
  metrics: {
    favorites: number
    applications: number
    visitsRequests: number
    pendingVisitRequests: number
    listings: number
  }
  listingFavorites: Array<{
    listingId: number | string
    favoritesCount: number
  }>
  applications: Array<{
    id: number
    property_fk: number
    property_title?: string | null
    applicant_user_fk?: string | null
    applicant_name?: string | null
    applicant_email?: string | null
    move_in_date?: string | null
    income_range?: string | null
    occupants?: number | null
    has_pets?: string | null
    message?: string | null
    application_status?: ApplicationPipelineStatus
    notes_owner?: string | null
    owner_message?: string | null
    created_at?: string
    updated_at?: string | null
  }>
  pendingVisitsByListing: Array<{
    listingId: number | string
    count: number
  }>
}

export async function loadOwnerListingManager(
  listingId: string
): Promise<ListingManagerPayload> {
  const res = await axiosInstance.get<ApiResponse<ListingManagerPayload>>(
    ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
    { params: { listingId } }
  )
  return ensureSuccess(res.data)
}

export async function loadOwnerDashboardSummary(): Promise<OwnerDashboardSummary> {
  try {
    const res = await axiosInstance.get<ApiResponse<OwnerDashboardSummary>>(
      ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
      { params: { scope: 'ownerDashboard' } }
    )
    return ensureSuccess(res.data)
  } catch (err: unknown) {
    const axiosErr = err as {
      response?: { data?: { code?: string; role?: string; message?: string } }
    }
    if (axiosErr?.response?.data?.code === 'email_not_verified') {
      const gated = new Error(
        axiosErr.response.data.message || 'Email confirmation required'
      ) as Error & { code?: string; role?: string }
      gated.code = 'email_not_verified'
      gated.role = axiosErr.response.data.role || 'owner'
      throw gated
    }
    throw err
  }
}

export async function updateOwnerListingStatus(
  listingId: string,
  status: ListingManagerStatus
): Promise<ListingManagerPayload> {
  const res = await axiosInstance.post<ApiResponse<ListingManagerPayload>>(
    ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
    { action: 'updateListingStatus', listingId, status }
  )
  return ensureSuccess(res.data)
}

export async function createVisitSlots(params: {
  listingId: string
  slots: Array<{ startAt: string; endAt: string; capacity?: number; notes?: string }>
  weeklyRepeatWeeks?: number
}): Promise<ListingManagerPayload> {
  const res = await axiosInstance.post<ApiResponse<ListingManagerPayload>>(
    ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
    { action: 'createVisitSlots', ...params }
  )
  return ensureSuccess(res.data)
}

export async function updateVisitSlot(params: {
  listingId: string
  slotId: number
  status?: VisitSlotStatus
  capacity?: number
  notes?: string
}): Promise<ListingManagerPayload> {
  const res = await axiosInstance.post<ApiResponse<ListingManagerPayload>>(
    ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
    { action: 'updateVisitSlot', ...params }
  )
  return ensureSuccess(res.data)
}

export async function updateVisitRequestStatus(params: {
  listingId: string
  requestId: number
  status: VisitRequestStatus
  targetSlotId?: number
}): Promise<ListingManagerPayload> {
  const res = await axiosInstance.post<ApiResponse<ListingManagerPayload>>(
    ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
    { action: 'updateVisitRequestStatus', ...params }
  )
  return ensureSuccess(res.data)
}

export async function updateOwnerApplication(params: {
  listingId: string
  applicationId: number
  status: ApplicationPipelineStatus
  notesOwner?: string
  ownerMessage?: string
}): Promise<ListingManagerPayload> {
  const res = await axiosInstance.post<ApiResponse<ListingManagerPayload>>(
    ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
    { action: 'updateApplication', ...params }
  )
  return ensureSuccess(res.data)
}

export async function loadListingVisitSlots(listingId: string) {
  const res = await axiosInstance.get<ApiResponse<Array<{
    id: number
    listing_id: number
    start_at: string
    end_at: string
    capacity: number
    status: VisitSlotStatus
    notes?: string | null
  }>>>(ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER, {
    params: { listingId, scope: 'visitSlots' },
  })
  return ensureSuccess(res.data)
}

export async function createVisitRequest(params: {
  listingId: string
  slotId: number
  message?: string
}): Promise<ListingManagerPayload> {
  const res = await axiosInstance.post<ApiResponse<ListingManagerPayload>>(
    ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER,
    { action: 'createVisitRequest', ...params }
  )
  return ensureSuccess(res.data)
}

export async function deleteOwnerListing(listingId: string): Promise<void> {
  const res = await axiosInstance.delete<ApiResponse<unknown>>(ENDPOINTS.PROPERTY.OWNER_LISTING_MANAGER, {
    data: { action: 'deleteListing', listingId },
  })
  if (!res.data?.success) {
    throw new Error(res.data?.message || 'Request failed')
  }
}
